import { DynamicIcon } from 'lucide-react/dynamic';
import React from 'react';
import { Card, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { Card as CardType, GoalWithStatus } from '../../service/api';
import CardActionsList from '../CardActionsList';

interface GoalProps {
  goalData: GoalWithStatus;
  onClick: () => void;
}

const Goal: React.FC<GoalProps> = ({ goalData, onClick }) => {
  const { goal, status, count } = goalData;
  const { t } = useTranslation();

  return (
    <Card
      text="white"
      className="mb-3 position-relative"
      style={{
        backgroundColor: 'rgba(6, 56, 96, 0.85)',
        cursor: 'pointer',
        border: '1px solid rgba(255, 255, 255, 0.1)',
      }}
      onClick={onClick}
    >
      <Card.Body className="py-3">
        <Row className="align-items-center">
          <Col xs="auto">
            <div className="position-relative">
              <div
                className="d-flex align-items-center justify-content-center rounded-3 bg-primary"
                style={{ width: '3rem', height: '3rem', flexShrink: 0 }}
              >
                <DynamicIcon
                  name={goal.icon}
                  size={24}
                  strokeWidth={1.5}
                  className="text-white"
                />
              </div>
              {count > 0 && (
                <span
                  className="position-absolute top-100 start-100 translate-middle badge rounded-pill bg-danger text-white"
                  style={{ fontSize: '0.7rem', minWidth: '1.5rem' }}
                >
                  x{count}
                </span>
              )}
            </div>
          </Col>
          <Col>
            <div className="d-flex align-items-center justify-content-between">
              <div className="flex-grow-1">
                <h3 className="h5 fw-bold mb-1 card-title">{goal.title}</h3>
                <div className="d-flex align-items-center gap-2">
                  {status.isCompleted && (
                    <span className="badge bg-success fs-7">
                      ✓ {t('goals.completed')}
                    </span>
                  )}
                  {goal.config.repeatable === false && (
                    <span className="badge bg-info fs-7">
                      {t('goals.unique')}
                    </span>
                  )}
                  {goal.config.sequenceType === 'sequential' && (
                    <span className="badge bg-info fs-7">
                      {t('goals.sequential')}
                    </span>
                  )}
                </div>
              </div>
              {typeof goal.rewardCard !== 'string' &&
                (goal.rewardCard as CardType).actions?.length > 0 && (
                  <CardActionsList
                    actions={(goal.rewardCard as CardType).actions}
                    size="sm"
                    className="ms-3"
                  />
                )}
            </div>
          </Col>
        </Row>

        {/* Progress indicator for incomplete goals */}
        {!status.isCompleted && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <small className="text-white-50">
                {t('goals.progress')}: {status.current}/{status.required}
              </small>
              <small className="text-white-50">
                {Math.round(status.percentage)}%
              </small>
            </div>
            <div className="progress" style={{ height: '6px' }}>
              <div
                className="progress-bar bg-primary"
                role="progressbar"
                style={{ width: `${status.percentage}%` }}
                aria-valuenow={status.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default Goal;
